import { G, clamp, lerp } from '../core/math';
import * as K from './constants';
import type { GripPatch } from './constants';
import { realismById, type RealismProfile } from './realism';

export interface KartState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  heading: number;
  yawRate: number;
  steer: number;
  /** Rear wheels locked under braking. Latched, with hysteresis. */
  locked: boolean;
  /** 0..1 how close the front tyres are to their limit. Drives understeer cues. */
  frontLoad: number;
  /** 0..1 how close the rear tyres are to theirs. Drives oversteer cues. */
  rearLoad: number;
  /** Body-frame lateral velocity, for smoke and skid marks. */
  slip: number;
  /** Previous tick's longitudinal acceleration, used for weight transfer. */
  lastLongAccel: number;
}

/**
 * Diff-less scrub assist: a solid rear axle forces both rear wheels to the
 * same rotational speed. Under power in a corner the inside wheel — carrying
 * less load and needing less force to keep pace — gets dragged rather than
 * cleanly driven, and that mismatch produces a small yaw moment that helps
 * rotate the kart into the corner. It is the felt, rotational half of "no
 * differential"; the other half (inside-rear lift, and the lateral grip a
 * transferred load costs) is not modelled — see vehicle.ts's stepKart doc.
 *
 * Deliberately a *moment* only, never a lateral or longitudinal force: it
 * changes how eagerly the kart rotates, not how much cornering speed a corner
 * can be carried at, so the solved ideal lap (built on `mu * G` alone) stays
 * exactly what it was.
 */
const SCRUB_YAW_GAIN = 0.35;

export interface Controls {
  steer: number; // -1 left .. +1 right
  throttle: number; // 0..1
  brake: number; // 0..1
  /** Reverse drive 0..1, set by the session once the kart is at a standstill
   *  with the brake held. Not part of the recorded trace — replay re-derives
   *  it from the same (brakeByte, speed) state, so it never widens the wire
   *  contract. Omitted means 0 (no reverse), so callers that only build a
   *  {steer, throttle, brake} trio still type-check. */
  reverse?: number;
}

export function createKart(x: number, y: number, heading: number): KartState {
  return {
    x,
    y,
    vx: 0,
    vy: 0,
    heading,
    yawRate: 0,
    steer: 0,
    locked: false,
    frontLoad: 0,
    rearLoad: 0,
    slip: 0,
    lastLongAccel: 0,
  };
}

export function speedOf(s: KartState): number {
  return Math.hypot(s.vx, s.vy);
}

/**
 * Advance one fixed tick.
 *
 * A bicycle model with a friction ellipse on the rear axle only. Two failure
 * modes fall out of that single asymmetry:
 *
 *   too fast, no brake  -> front slip angle exceeds PEAK_SLIP_FRONT, front force
 *                          clips, the kart understeers and runs wide
 *   hard brake, turned  -> brake force consumes the rear ellipse, rear lateral
 *                          capacity collapses, the back steps out
 *
 * Braking early and straight, then trailing off as you turn in, keeps both axles
 * inside their limits. That is the fastest way round and it is also correct
 * technique, which is the whole point of the design.
 *
 * Deliberately not modelled: lateral load transfer, and the grip it costs a
 * tyre pair carrying load unevenly. The solved ideal lap (`speedprofile.ts`)
 * assumes every corner is takeable at exactly `mu * G`, and that assumption
 * runs through the whole game — medals, objectives, drills, AI pace. Making
 * the vehicle model load transfer honestly would make it fall short of that
 * figure in every corner, which is a solver-wide change, not a vehicle one.
 * `SCRUB_YAW_GAIN` above adds the rotational half of "no differential"
 * without touching how much speed a corner can be carried at, which is why
 * that piece is safe to add here alone.
 */
/**
 * How much barrier damage bleeds a kart's performance. 1 = undamaged.
 * Grip (both axles) suffers first, power a little less — a damaged kart is
 * harder to turn in and barely gets going, which is the whole penalty.
 */
function healthGrip(health: number): number {
  return 1 - (1 - Math.max(0, health)) * 0.25;
}
function healthPower(health: number): number {
  return 1 - (1 - Math.max(0, health)) * 0.15;
}

export function stepKart(
  s: KartState,
  c: Controls,
  surface: GripPatch,
  realism: RealismProfile = realismById('sport'),
  dt: number = K.TICK_DT,
  /** Aerodynamic drag multiplier, 1 = none reduced. Grand Prix slipstream only —
   *  every other caller omits this and gets ordinary, undrafted drag. */
  dragScale: number = 1,
  /** Kart integrity 0..1, 1 = undamaged. Scales grip and power as damage. */
  health: number = 1,
): void {
  // Damage scales grip (both axles) and, a little less, power.
  const hp = healthGrip(health);
  const powerP = healthPower(health);

  // --- steering actuator ------------------------------------------------
  const targetSteer = clamp(c.steer, -1, 1) * K.MAX_STEER;
  const maxDelta = K.STEER_RATE * dt;
  s.steer += clamp(targetSteer - s.steer, -maxDelta, maxDelta);

  const cosH = Math.cos(s.heading);
  const sinH = Math.sin(s.heading);

  // Body frame: u along the nose, w out the left side.
  const u = s.vx * cosH + s.vy * sinH;
  const w = -s.vx * sinH + s.vy * cosH;
  const speed = Math.hypot(s.vx, s.vy);

  const mu = surface.mu;

  // --- longitudinal weight transfer -------------------------------------
  // Braking unloads the rear, which is the axle doing the braking, so the limit
  // recedes as you lean on it. Modulation matters.
  //
  // Driven from the previous tick's acceleration rather than this tick's demand:
  // load limits the forces and the forces set the load, and at 120 Hz lagging it
  // by one step resolves that circularity without an inner solve.
  const staticFront = (K.MASS * G * K.B_CG) / K.WHEELBASE;
  const staticRear = (K.MASS * G * K.A_CG) / K.WHEELBASE;
  const transfer = (s.lastLongAccel * K.MASS * K.CG_HEIGHT) / K.WHEELBASE;
  const loadFront = Math.max(0, staticFront - transfer);
  const loadRear = Math.max(0, staticRear + transfer);

  const capFront = mu * loadFront * hp;
  const capRear = mu * loadRear * hp;

  // --- slip angles -------------------------------------------------------
  const denom = Math.max(Math.abs(u), K.SLIP_DENOM_FLOOR);
  const alphaFront = Math.atan2(w + s.yawRate * K.A_CG, denom) - s.steer;
  const alphaRear = Math.atan2(w - s.yawRate * K.B_CG, denom);

  // --- lateral tyre forces: linear, then clipped at the limit -------------
  // The clip is the grip limit. Nothing else in this file models "losing grip".
  const stiffFront = capFront / K.PEAK_SLIP_FRONT;
  const stiffRear = capRear / K.PEAK_SLIP_REAR;

  const rawFront = -stiffFront * alphaFront;
  const rawRear = -stiffRear * alphaRear;

  const fyFront = clamp(rawFront, -capFront, capFront);
  let fyRear = clamp(rawRear, -capRear, capRear);

  // --- rear friction ellipse ---------------------------------------------
  // Braking and throttle divide the rear's grip budget in opposite ways, and the
  // asymmetry is deliberate.
  //
  // Braking takes priority and is allowed to overwhelm the tyres — that is the
  // whole mechanic. Throttle is automatic, so it takes only what cornering has
  // left spare; otherwise holding the loud pedal flat would cap the kart at ~70%
  // of its grip and the solved ideal lap could never be approached.
  let brakeForce = clamp(c.brake, 0, 1) * K.MAX_BRAKE_FORCE * (u > 0.2 ? 1 : 0);
  let fx: number;

  // Reverse: only trusted once the session has confirmed a standstill with the
  // brake held, and only while the kart is not actually moving forward. The
  // wheels steer as normal — with a backward signed speed the kinematic model
  // already yaws the kart the physically correct way, so no special steering.
  const reverseDrive = c.reverse ?? 0;
  const reversing = reverseDrive > 0 && u < 0.2;
  if (reversing) {
    s.locked = false;
    const backSpeed = Math.max(0, -u);
    const canPush = backSpeed < K.REVERSE_MAX_SPEED ? 1 : 0;
    const spare = Math.sqrt(Math.max(0, capRear * capRear - fyRear * fyRear));
    const wantedRev = K.REVERSE_ACCEL * K.MASS * reverseDrive * canPush;
    fx = -Math.min(wantedRev, Math.max(spare, 1));
  } else if (brakeForce > 0) {
    // Lock-up: demand beyond what the rear can hold stops the wheels turning.
    //
    // Tested on *combined* demand — brake force and cornering force together
    // against the rear's capacity. Braking hard in a straight line stays just
    // inside it; the same pedal with the wheel turned does not, and the rears
    // let go. That is the entire mechanic, and it falls out of the ellipse
    // rather than needing a special case.
    //
    // Compared against the steady-state capacity rather than the instantaneous
    // one: using the live figure meant the load dip from the initial bite
    // tripped the lock on any firm press, and hysteresis then held it there.
    const lockThreshold = K.dynamicRearCapacity(mu) * hp;

    // Longitudinal room left after cornering has taken its share of the circle.
    // Everything below is measured against this rather than against combined
    // demand: lateral force alone can exceed the steady-state threshold in a
    // hard corner, and testing the combination meant the rears "locked" from
    // pure cornering with no brake applied at all.
    const lateralUse = Math.min(Math.abs(fyRear), lockThreshold);
    const longRoom = Math.sqrt(Math.max(0, lockThreshold * lockThreshold - lateralUse * lateralUse));

    // Brake assist bleeds pressure to stay inside that room — exactly what a
    // driver's foot does trailing into a corner. In a straight line there is no
    // lateral demand to make way for, so full braking survives untouched and
    // maxBrakeDecel is unchanged. That is what keeps the ideal lap common to
    // every mode.
    if (realism.brakeAssist) brakeForce = Math.min(brakeForce, longRoom * 0.98);

    // How far past the limit the pedal is asking the rears to go. With no
    // meaningful pedal there is nothing to lock, however little room is left —
    // otherwise cornering at the limit with the brakes bled to nothing would
    // still register as a lock-up.
    const over =
      brakeForce < 1 ? 0 : longRoom > 1 ? brakeForce / longRoom : Infinity;
    const slide = clamp((over - 1) / realism.lockProgression, 0, 1);
    s.locked = slide > 0.5;

    // Gripping: the tyre holds, and braking eats into its lateral budget.
    const fxGrip = -Math.min(brakeForce, longRoom);
    const gripLateralCap = Math.sqrt(Math.max(0, capRear * capRear - fxGrip * fxGrip));
    const fyGrip = clamp(fyRear, -gripLateralCap, gripLateralCap);

    // Sliding: a locked tyre is not frictionless. Sliding friction acts opposite
    // the contact patch's slip velocity, split between longitudinal and lateral
    // by that velocity's direction.
    //
    // Zeroing the lateral force instead — as this did originally — left the rear
    // axle with nothing at all, so the kart pivoted about the front the instant
    // the rears went, with no gradient between gripping and gone.
    const slipX = u;
    const slipY = w - s.yawRate * K.B_CG;
    const slipMag = Math.hypot(slipX, slipY) || 1;
    const slideForce = mu * loadRear * realism.lockedGrip;
    const fxSlide = (-slideForce * slipX) / slipMag;
    const fySlide = (-slideForce * slipY) / slipMag;

    // Blend rather than switch. A real tyre's force falls away over a range of
    // slip, and that range is the window a driver has to feel the back going and
    // do something about it. Snapping between the two states removes the window
    // entirely, which is what made this uncatchable.
    fx = lerp(fxGrip, fxSlide, slide);
    fyRear = lerp(fyGrip, fySlide, slide);
  } else {
    s.locked = false;
    const uForPower = Math.max(Math.abs(u), 1.0);
    // Power-limited by the engine alone; the *tyre* limit is `spare`, below —
    // it already carries this tick's own weight-transferred `capRear`, so
    // capping `wanted` at the static MAX_TRACTION_FORCE too would double-count
    // the limit and throw away exactly the transfer benefit that lets the
    // solver's own `maxDriveAccel` assume more than a static rear load allows.
    const wanted = clamp(c.throttle, 0, 1) * ((K.ENGINE_POWER * powerP) / uForPower);
    const spare = Math.sqrt(Math.max(0, capRear * capRear - fyRear * fyRear));
    const driveForce = Math.min(wanted, spare);

    // Direct drive has no clutch or freewheel: off the throttle, the chain
    // still drags against engine compression rather than letting the kart
    // coast. In today's two-input scheme throttle is only ever 0 while also
    // braking (see `dequantise`), so this never actually fires in play — it
    // exists so this function stays physically honest for any caller that
    // does lift without braking, rather than silently coasting forever.
    const engineBrake =
      (1 - clamp(c.throttle, 0, 1)) * K.ENGINE_BRAKE_DECEL_PER_MS * K.MASS * Math.abs(u);
    fx = driveForce - engineBrake;
  }

  s.frontLoad = capFront > 1 ? Math.min(1, Math.abs(rawFront) / capFront) : 0;
  s.rearLoad = capRear > 1 ? Math.min(1, Math.hypot(rawRear, fx) / capRear) : 0;
  s.slip = w;

  // --- resolve into the body frame ---------------------------------------
  // The front tyre force acts perpendicular to the steered wheel, so part of it
  // resists forward motion. That is where steering drag comes from.
  const fyFrontBody = fyFront * Math.cos(s.steer);
  const fxFrontBody = -fyFront * Math.sin(s.steer);

  // Only the aerodynamic term is drafted — rolling resistance and surface drag
  // come from the tyres and the road, not the air, and a slipstream does
  // nothing about either.
  const dragForce = K.DRAG_K * dragScale * speed * speed + K.ROLLING_RESIST + surface.extraDrag;
  const dragSign = u >= 0 ? 1 : -1;

  const fxTotal = fx + fxFrontBody - dragForce * dragSign;
  const fyTotal = fyFrontBody + fyRear;

  const axBody = fxTotal / K.MASS;
  const ayBody = fyTotal / K.MASS;
  s.lastLongAccel = axBody;

  let yawMoment = K.A_CG * fyFrontBody - K.B_CG * fyRear;

  // Diff-less scrub assist — see the constant's own doc. Only under genuine
  // drive force, so it never touches the braking-abuse behaviour the realism
  // modes are tuned and tested against.
  if (fx > 0) {
    yawMoment += fx * K.REAR_TRACK * 0.5 * SCRUB_YAW_GAIN * clamp(s.steer / K.MAX_STEER, -1, 1);
  }

  // Stability assist: a corrective moment opposing rear slip, the way ESC works.
  // Saturates at a quarter radian of slip so it is a helping hand rather than an
  // autopilot, and does nothing below walking pace.
  if (realism.stabilityAssist > 0 && speed > 4) {
    // Only the slip *beyond* the rear tyre's peak counts. Cornering hard needs
    // rear slip to generate force at all, so correcting all of it quietly capped
    // how much grip the kart could reach — which would break the promise that
    // every mode shares the same limits and the same braking points.
    //
    // Sign follows the rear axle's own restoring moment (-B_CG * fyRear, which
    // works out proportional to +alphaRear). Backwards here amplifies a slide
    // instead of catching it.
    const excess = alphaRear - clamp(alphaRear, -K.PEAK_SLIP_REAR, K.PEAK_SLIP_REAR);
    yawMoment += clamp(excess / 0.2, -1, 1) * realism.stabilityAssist * 1400 * K.YAW_AUTHORITY_SCALE;
  }

  const yawAccel = yawMoment / K.YAW_INERTIA;

  // --- integrate ----------------------------------------------------------
  s.vx += (axBody * cosH - ayBody * sinH) * dt;
  s.vy += (axBody * sinH + ayBody * cosH) * dt;

  s.yawRate += yawAccel * dt;

  // Solid rear axle resists rotation, more so on power. The mode scales how much
  // of that damping there is — it is the difference between a slide you can hold
  // and one that is gone before you react.
  const yawDamp =
    (K.YAW_DAMP_BASE + K.YAW_DAMP_THROTTLE * clamp(c.throttle, 0, 1)) *
    realism.yawDampScale *
    K.YAW_AUTHORITY_SCALE;
  s.yawRate -= s.yawRate * Math.min(1, yawDamp * dt);

  // Below walking pace the slip-angle model is meaningless; steer kinematically.
  if (speed < K.LOWSPEED_MAX) {
    const kinematic = (u * Math.tan(s.steer)) / K.WHEELBASE;
    const t = clamp(speed / K.LOWSPEED_MAX, 0, 1);
    s.yawRate = lerp(kinematic, s.yawRate, t);
  }

  s.heading += s.yawRate * dt;
  s.x += s.vx * dt;
  s.y += s.vy * dt;

  // Kill numerical creep so a stopped kart stays stopped — unless it is
  // trying to reverse, which starts from a standstill and must not be zeroed.
  if (!reversing && Math.hypot(s.vx, s.vy) < 0.05 && c.throttle < 0.01) {
    s.vx = 0;
    s.vy = 0;
    s.yawRate = 0;
  }
}
